import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchDashboardStats } from '../store/slices/dashboardSlice';
import { fetchMachines, syncMachineJobs } from '../store/slices/machinesSlice';

const StatusBadge = ({ status }) => {
  const statusColors = {
    new: 'primary',
    in_queue: 'info',
    assigned: 'secondary',
    in_production: 'warning',
    quality_check: 'info',
    ready_for_shipping: 'success',
    shipped: 'success',
    on_hold: 'danger',
    cancelled: 'dark'
  };

  const statusLabels = {
    new: 'Nieuw',
    in_queue: 'In wachtrij',
    assigned: 'Toegewezen',
    in_production: 'In productie',
    quality_check: 'Quality check',
    ready_for_shipping: 'Gereed',
    shipped: 'Verzonden',
    on_hold: 'On hold',
    cancelled: 'Geannuleerd'
  };

  return (
    <span className={`badge bg-${statusColors[status] || 'secondary'}`}>
      {statusLabels[status] || status}
    </span>
  );
};

const MachineStatusIndicator = ({ status }) => {
  const colors = {
    idle: 'secondary',
    active: 'success',
    maintenance: 'warning',
    error: 'danger',
    offline: 'dark'
  };

  return (
    <span className={`status-dot bg-${colors[status] || 'secondary'}`}></span>
  );
};

const Dashboard = () => {
  const dispatch = useDispatch();
  const { stats, loading: statsLoading } = useSelector((state) => state.dashboard);
  const { machines, loading: machinesLoading } = useSelector((state) => state.machines);

  useEffect(() => {
    dispatch(fetchDashboardStats());
    dispatch(fetchMachines());
  }, [dispatch]);

  const kpiCards = [
    {
      title: 'Nieuwe orders',
      value: stats?.ordersByStatus?.new || 0,
      icon: 'bi-inbox',
      color: 'primary',
      link: '/orders?status=new'
    },
    {
      title: 'In productie',
      value: stats?.ordersByStatus?.in_production || 0,
      icon: 'bi-gear',
      color: 'warning',
      link: '/orders?status=in_production'
    },
    {
      title: 'Gereed',
      value: stats?.ordersByStatus?.ready_for_shipping || 0,
      icon: 'bi-check-circle',
      color: 'success',
      link: '/orders?status=ready_for_shipping'
    },
    {
      title: 'On hold',
      value: stats?.ordersByStatus?.on_hold || 0,
      icon: 'bi-pause-circle',
      color: 'danger',
      link: '/orders?status=on_hold'
    }
  ];

  return (
    <div className="dashboard">
      <div className="page-header">
        <h1>Dashboard</h1>
        <p className="text-muted">Overzicht productie status</p>
      </div>

      {/* KPI Cards */}
      <div className="row mb-4">
        {kpiCards.map((card, index) => (
          <div key={index} className="col-md-6 col-lg-3 mb-3">
            <Link to={card.link} className="text-decoration-none">
              <div className={`card kpi-card border-${card.color}`}>
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <p className="text-muted mb-1">{card.title}</p>
                      <h2 className={`mb-0 text-${card.color}`}>
                        {statsLoading ? '-' : card.value}
                      </h2>
                    </div>
                    <div className={`kpi-icon bg-${card.color}`}>
                      <i className={`bi ${card.icon}`}></i>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        ))}
      </div>

      <div className="row">
        {/* Machine Status */}
        <div className="col-lg-6 mb-4">
          <div className="card h-100">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Machine Status</h5>
              <div className="d-flex gap-2">
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={async () => {
                    await dispatch(syncMachineJobs());
                    dispatch(fetchMachines());
                  }}
                  title="Synchroniseer jobs met machines"
                >
                  <i className="bi bi-arrow-repeat"></i>
                </button>
                <Link to="/machines" className="btn btn-sm btn-outline-primary">
                  Bekijk alle
                </Link>
              </div>
            </div>
            <div className="card-body">
              {machinesLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Laden...</span>
                  </div>
                </div>
              ) : !machines || machines.length === 0 ? (
                <p className="text-muted text-center py-4">Geen machines geconfigureerd</p>
              ) : (
                <div className="machine-list">
                  {(() => {
                    // Calculate total orders across all machines
                    const totalOrders = machines.reduce((sum, m) => {
                      const machineOrders = (m.currentJob ? 1 : 0) + (m.queue?.length || 0);
                      return sum + machineOrders;
                    }, 0);

                    return machines.slice(0, 6).map((machine) => {
                      const machineOrders = (machine.currentJob ? 1 : 0) + (machine.queue?.length || 0);
                      const percentage = totalOrders > 0 ? Math.round((machineOrders / totalOrders) * 100) : 0;

                      return (
                        <div key={machine._id} className="machine-item">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <div className="d-flex align-items-center">
                              <MachineStatusIndicator status={machine.status} />
                              <span className="fw-medium">{machine.name}</span>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <span className="text-muted small">{machineOrders} orders</span>
                              <span className="fw-bold" style={{ minWidth: '40px', textAlign: 'right' }}>{percentage}%</span>
                            </div>
                          </div>
                          <div className="progress" style={{ height: '8px' }}>
                            <div
                              className={`progress-bar ${machine.status === 'active' ? 'bg-success' : 'bg-primary'}`}
                              role="progressbar"
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recent Orders */}
        <div className="col-lg-6 mb-4">
          <div className="card h-100">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Recente Orders</h5>
              <Link to="/orders" className="btn btn-sm btn-outline-primary">
                Bekijk alle
              </Link>
            </div>
            <div className="card-body p-0">
              {statsLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Laden...</span>
                  </div>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead>
                      <tr>
                        <th>Order</th>
                        <th>Klant</th>
                        <th>Status</th>
                        <th>Datum</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats?.recentOrders?.slice(0, 5).map((order) => (
                        <tr key={order._id}>
                          <td>
                            <Link to={`/orders/${order._id}`}>
                              {order.orderNumber}
                            </Link>
                          </td>
                          <td>{order.customer?.name || '-'}</td>
                          <td><StatusBadge status={order.status} /></td>
                          <td className="text-muted small">
                            {new Date(order.createdAt).toLocaleDateString('nl-NL')}
                          </td>
                        </tr>
                      )) || (
                        <tr>
                          <td colSpan="4" className="text-center text-muted py-4">
                            Geen recente orders
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions & Alerts */}
      <div className="row">
        <div className="col-lg-6 mb-4">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Snelle acties</h5>
            </div>
            <div className="card-body">
              <div className="d-grid gap-2">
                <Link to="/cutlists" className="btn btn-outline-primary">
                  <i className="bi bi-scissors me-2"></i>
                  Nieuwe snijlijst maken
                </Link>
                <Link to="/machines" className="btn btn-outline-primary">
                  <i className="bi bi-gear-wide-connected me-2"></i>
                  Machines beheren
                </Link>
                <Link to="/settings" className="btn btn-outline-secondary">
                  <i className="bi bi-arrow-repeat me-2"></i>
                  Picqer synchroniseren
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-6 mb-4">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Picqer Synchronisatie</h5>
              {stats?.picqerSync?.enabled && (
                <span className="badge bg-success">Actief</span>
              )}
            </div>
            <div className="card-body">
              {stats?.picqerSync?.enabled ? (
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span className="text-muted">Laatste sync:</span>
                    <span className={`fw-medium ${stats.picqerSync.lastSyncStatus === 'error' ? 'text-danger' : ''}`}>
                      {stats.picqerSync.lastSyncAt
                        ? new Date(stats.picqerSync.lastSyncAt).toLocaleString('nl-NL', {
                            day: '2-digit',
                            month: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                          })
                        : 'Nog niet gesynchroniseerd'}
                    </span>
                  </div>
                  {stats.picqerSync.lastSyncMessage && (
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <span className="text-muted">Resultaat:</span>
                      <span className={stats.picqerSync.lastSyncStatus === 'error' ? 'text-danger' : 'text-success'}>
                        {stats.picqerSync.lastSyncStatus === 'error' ? (
                          <><i className="bi bi-x-circle me-1"></i>{stats.picqerSync.lastSyncMessage}</>
                        ) : (
                          <><i className="bi bi-check-circle me-1"></i>{stats.picqerSync.lastSyncMessage}</>
                        )}
                      </span>
                    </div>
                  )}
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <span className="text-muted">Interval:</span>
                    <span>Elke {stats.picqerSync.syncInterval} minuten</span>
                  </div>
                  {stats.picqerSync.nextSyncAt && (
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <span className="text-muted">Volgende sync:</span>
                      <span>
                        {new Date(stats.picqerSync.nextSyncAt).toLocaleTimeString('nl-NL', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                  )}

                  {stats.picqerSync.fullSyncEnabled && (
                    <>
                      <hr className="my-2" />
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="text-muted">Volledige sync:</span>
                        <span className="badge bg-info">
                          {stats.picqerSync.fullSyncDay === 'daily' ? 'Dagelijks' :
                           stats.picqerSync.fullSyncDay.charAt(0).toUpperCase() + stats.picqerSync.fullSyncDay.slice(1)} om {String(stats.picqerSync.fullSyncHour).padStart(2, '0')}:00
                        </span>
                      </div>
                      {stats.picqerSync.lastFullSyncAt && (
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="text-muted">Laatste volledige:</span>
                          <span className={stats.picqerSync.lastFullSyncStatus === 'error' ? 'text-danger' : ''}>
                            {new Date(stats.picqerSync.lastFullSyncAt).toLocaleString('nl-NL', {
                              day: '2-digit',
                              month: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                      )}
                      {stats.picqerSync.nextFullSyncAt && (
                        <div className="d-flex justify-content-between align-items-center">
                          <span className="text-muted">Volgende volledige:</span>
                          <span>
                            {new Date(stats.picqerSync.nextFullSyncAt).toLocaleString('nl-NL', {
                              weekday: 'short',
                              day: '2-digit',
                              month: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div className="text-center text-muted py-3">
                  <i className="bi bi-cloud-slash fs-1 d-block mb-2"></i>
                  <p className="mb-2">Picqer sync niet actief</p>
                  <Link to="/settings" className="btn btn-sm btn-outline-primary">
                    Configureren
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-lg-6 mb-4">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Waarschuwingen</h5>
            </div>
            <div className="card-body">
              {stats?.alerts?.length > 0 ? (
                <div className="alert-list">
                  {stats.alerts.map((alert, index) => (
                    <div
                      key={index}
                      className={`alert alert-${alert.type || 'warning'} mb-2`}
                    >
                      <i className={`bi ${alert.icon || 'bi-exclamation-triangle'} me-2`}></i>
                      {alert.message}
                      {alert.link && (
                        <Link to={alert.link} className="alert-link ms-2">Bekijken</Link>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-muted py-4">
                  <i className="bi bi-check-circle fs-1 d-block mb-2"></i>
                  Geen waarschuwingen
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Production Statistics */}
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Productie Statistieken</h5>
            </div>
            <div className="card-body">
              <div className="row text-center">
                <div className="col-md-3 mb-3">
                  <h3 className="text-primary">{stats?.todayStats?.processed || 0}</h3>
                  <p className="text-muted mb-0">Vandaag verwerkt</p>
                </div>
                <div className="col-md-3 mb-3">
                  <h3 className="text-success">{stats?.todayStats?.shipped || 0}</h3>
                  <p className="text-muted mb-0">Vandaag verzonden</p>
                </div>
                <div className="col-md-3 mb-3">
                  <h3 className="text-warning">{stats?.todayStats?.inQueue || 0}</h3>
                  <p className="text-muted mb-0">In wachtrij</p>
                </div>
                <div className="col-md-3 mb-3">
                  <h3 className="text-info">{stats?.totalOrders || 0}</h3>
                  <p className="text-muted mb-0">Totaal orders</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
